// Dragging the start. The start is drawn by the map itself (the planner's start sprite, or the
// ringed My-location dot), not a DOM marker like the measured point, so it is dragged the way
// Mapbox drags a feature: press on its layer, follow the pointer, let go.

/** The layers that draw the start, in the order they are hit-tested. */
export const START_HANDLE_LAYERS = ['measure-start-pin', 'measure-my-location-ring', 'measure-my-location'];

export interface StartDragHandlers {
  onDragStart: () => void;
  onDrag: (lngLat: { lng: number; lat: number }) => void;
  onDragEnd: () => void;
  /** The cursor to put back when the pointer leaves the start. */
  restCursor: () => string;
}

export class MeasureStartDrag {
  private active = false;
  private hovering = false;
  private readonly bound: { type: string; fn: (event: any) => void }[] = [];

  constructor(private readonly map: any, private readonly handlers: StartDragHandlers) {}

  get dragging(): boolean {
    return this.active;
  }

  attach(): void {
    // One listener over all three layers, so moving between the ring and the dot isn't a leave.
    this.listen('mouseenter', this.onEnter);
    this.listen('mouseleave', this.onLeave);
    this.listen('mousedown', this.onMouseDown);
    this.listen('touchstart', this.onTouchStart);
  }

  detach(): void {
    this.bound.forEach(({ type, fn }) => this.map.off(type, START_HANDLE_LAYERS, fn));
    this.bound.length = 0;
    this.stop();
  }

  /** Whether a map event landed on the start, so a click there doesn't move the measured point. */
  isOnStart(event: any): boolean {
    if (!event?.point || !this.map.queryRenderedFeatures) return false;
    const layers = START_HANDLE_LAYERS.filter((id) => this.map.getLayer?.(id));
    return layers.length > 0 && this.map.queryRenderedFeatures(event.point, { layers }).length > 0;
  }

  private listen(type: string, fn: (event: any) => void): void {
    this.map.on(type, START_HANDLE_LAYERS, fn);
    this.bound.push({ type, fn });
  }

  private setCursor(cursor: string): void {
    const canvas = this.map.getCanvas?.();
    if (canvas) canvas.style.cursor = cursor;
  }

  private readonly onEnter = () => {
    this.hovering = true;
    if (!this.active) this.setCursor('grab');
  };

  private readonly onLeave = () => {
    this.hovering = false;
    if (!this.active) this.setCursor(this.handlers.restCursor());
  };

  private readonly onMouseDown = (event: any) => {
    if (event.originalEvent?.button !== undefined && event.originalEvent.button !== 0) return;
    // Stops the map panning for this press.
    event.preventDefault();
    this.begin();
    this.map.on('mousemove', this.onMove);
    this.map.once('mouseup', this.onUp);
  };

  private readonly onTouchStart = (event: any) => {
    if (event.points && event.points.length !== 1) return;
    event.preventDefault();
    this.begin();
    this.map.on('touchmove', this.onMove);
    this.map.once('touchend', this.onUp);
  };

  private readonly onMove = (event: any) => {
    if (!this.active) return;
    this.handlers.onDrag({ lng: event.lngLat.lng, lat: event.lngLat.lat });
  };

  private readonly onUp = () => {
    if (!this.active) return;
    this.stop();
    this.handlers.onDragEnd();
  };

  private begin(): void {
    this.active = true;
    this.setCursor('grabbing');
    this.handlers.onDragStart();
  }

  private stop(): void {
    if (this.active) {
      this.map.off('mousemove', this.onMove);
      this.map.off('touchmove', this.onMove);
      this.map.off('mouseup', this.onUp);
      this.map.off('touchend', this.onUp);
    }
    this.active = false;
    this.setCursor(this.hovering ? 'grab' : this.handlers.restCursor());
  }
}
